import { animate, ImageView, View } from '@play-co/timestep-core/ui';
import createIntervalEmitter, {
  IntervalEmitter,
} from 'src/lib/createIntervalEmitter';
import { createEmitter, Emitter } from 'src/lib/Emitter';
import { animDuration, waitForItPromise } from 'src/lib/utils';
import { State } from 'src/state';
import { now } from 'src/StateObserver';

type NotificationCondition = (state: State, now: number) => boolean;

/**
 * Represents a badge UI component that can display an icon and supports visibility control
 * through emitters and animations. The badge consists of a background and an icon, and it
 * can be positioned and layered using z-index.
 *
 * The `Badge` class extends the `View` class and provides methods to:
 * - Add an emitter to control the badge's visibility based on a selector function.
 * - Animate the badge's appearance and disappearance using fade-in and fade-out effects.
 * - Periodically wiggle the icon to draw attention to the badge.
 *
 * Example usage:
 * ```typescript
 * const badge = new Badge({
 *   superview: parentView,
 *   x: 100,
 *   y: 200,
 *   zIndex: 2,
 * });
 *
 * badge.addEmitter((state, now) => state.shouldShowBadge, 1000);
 * ```
 *
 * @remarks
 * The badge uses `ImageView` components for its background and icon. The visibility
 * animations are handled using the `animate` utility, and the duration of animations
 * is determined by the `animDuration` constant.
 *
 * @example
 * Creating a badge and adding it to a parent view:
 * ```typescript
 * const badge = new Badge({
 *   superview: parentView,
 *   x: 50,
 *   y: 50,
 * });
 * ```
 *
 * Adding an emitter to control badge visibility:
 * ```typescript
 * badge.addEmitter((state, now) => state.isBadgeVisible);
 * ```
 */
export default class BadgeNotification extends View {
  private background: ImageView;
  private icon: ImageView;
  private emitter: IntervalEmitter<boolean> | Emitter<boolean> | undefined;
  private visible: boolean = false;

  /**
   * Constructs a new Badge instance.
   *
   * @param opts - Configuration options for the badge.
   * @param opts.superview - The parent view that this badge will be added to.
   * @param opts.zIndex - (Optional) The z-index of the badge. Defaults to 1.
   * @param opts.x - The x-coordinate position of the badge.
   * @param opts.y - The y-coordinate position of the badge.
   * @param opts.scale - (Optional) The scale of the badge. Defaults to 1.
   * @param opts.visible - (Optional) Initial visibility state of the badge. Defaults to true.
   */
  constructor(opts: {
    superview: View;
    zIndex?: number;
    x?: number;
    y?: number;
    scale?: number;
    visible?: boolean;
  }) {
    super(opts);

    this.visible = opts.visible ?? true;
    this.updateOpts({
      width: 43,
      height: 43,
      ...opts,
      canHandleEvents: false,
    });

    this.background = new ImageView({
      superview: this,
      image: `assets/ui/badge/badge.png`,
      width: 43,
      height: 43,
      centerAnchor: true,
      visible: opts.visible ?? false,
    });

    this.icon = new ImageView({
      superview: this.background,
      image: `assets/ui/badge/icon.png`,
      width: 16,
      height: 29,
      x: (this.background.style.width - 16) * 0.5,
      y: (this.background.style.height - 29) * 0.5,
      centerAnchor: true,
    });

    // Default emitter allow show badge without conditions
    this.emitter = createEmitter(this, () => this.style.visible);
  }

  /**
   * Adds an emitter that controls the visibility of a badge based on a selector function
   * and an optional interval. The emitter listens for changes and triggers the badge
   * to fade in or out accordingly.
   *
   * @param shouldShowSelector - A function that determines whether the badge should be shown.
   *                             It takes the current state and a timestamp as arguments and
   *                             returns a boolean.
   * @param interval - An optional interval in milliseconds. If provided, the emitter will
   *                   periodically evaluate the `shouldShowSelector` function. If not provided,
   *                   the `shouldShowSelector` function will be evaluated by selector only
   */
  public setEmitter(
    shouldShowSelector: NotificationCondition,
    interval?: number | undefined,
  ) {
    // If interval is not set, we use the current time to check if we should show the badge.
    const emitter = interval
      ? createIntervalEmitter(
          (state, now) => this.style.visible && shouldShowSelector(state, now),
          interval,
        )
      : createEmitter(
          this,
          (state) => this.style.visible && shouldShowSelector(state, now()),
        );

    // If shouldShowSelector is true and view is visible, we show the badge.
    emitter.addListener(async (show) => {
      if (show) {
        await this.showIn();
      } else {
        await this.fadeOut();
      }
    });

    this.emitter = emitter;
  }

  /**
   * Displays the badge notification with an optional delay.
   * If no delay is provided, it defaults to the animation duration
   * retrieved from `animDuration`.
   *
   * Once the delay has elapsed, the method invokes the parent class's
   * `show` method and triggers the `emitter`'s `force` method if the
   * emitter is defined.
   *
   * @param delay - Optional delay in milliseconds before showing the notification.
   */
  public override async show(delay?: number) {
    if (this.visible) {
      return;
    }

    this.visible = true;

    if (delay === undefined) {
      delay = animDuration;
    }

    await waitForItPromise(delay);

    super.show();
    this.emitter?.force();
  }

  /**
   * Hides the badge notification by initiating a fade-out animation.
   * Once the fade-out animation is complete, it calls the parent class's `hide` method.
   *
   * @remarks
   * This method uses `waitForIt` to delay the invocation of the parent `hide` method
   * until the fade-out animation duration has elapsed.
   *
   * @see {@link animDuration} for the animation duration.
   */
  public override async hide() {
    if (!this.style.visible) {
      return;
    }

    this.visible = false;

    await this.fadeOut();
    await waitForItPromise(animDuration);
    super.hide();
  }

  /**
   * Fades in the background element by animating its scale and opacity.
   * If the background is already visible, the method exits early.
   *
   * The animation duration is determined by the `animDuration` constant.
   * The animation starts with the background element being invisible and scaled down,
   * and transitions to full visibility and normal scale using an ease-out effect.
   * After fading in, the icon will automatically start wiggling periodically.
   *
   * @private
   */
  private async showIn() {
    if (this.background.style.visible) {
      return;
    }

    this.background.style.opacity = 0;
    this.background.style.scale = 0;
    this.background.style.visible = true;

    return animate(this.background)
      .clear()
      .then({ scale: 1, opacity: 1 }, animDuration, animate.easeOut)
      .toPromise();
  }

  /**
   * Fades out the background element by animating its scale and opacity to zero.
   * If the background is already hidden, the method exits early.
   *
   * The animation duration is determined by the `animDuration` constant.
   * Once the animation completes, the `hide` method is called to finalize the hiding process.
   *
   * @private
   */
  private async fadeOut() {
    if (!this.background.style.visible) {
      return;
    }

    return animate(this.background)
      .clear()
      .then({ scale: 0, opacity: 0 }, animDuration, animate.easeOut)
      .then(() => this.background.hide())
      .toPromise();
  }
}
